import { Pattern, ElementType } from '../api/jerseyApi';

// Form data interfaces
export interface PatternFormData {
  description: string;
  element_type_id: number;
  matching_string: string;
  absolute_positioning?: boolean | null; // legacy global
  absolute_positioning_front?: boolean | null; // null/undefined = auto
  absolute_positioning_side?: boolean | null; // null/undefined = auto
  is_logo?: boolean; // new field to mark logos
  status: boolean;
  front_svg_file?: File;
  side_svg_file?: File;
}

// Upload interfaces
export interface PatternUploadData {
  patternId: number;
  view: 'front' | 'side';
  file: File;
}

export interface PatternUploadResult {
  url: string;
  success: boolean;
  error?: string;
}

// Filter and search interfaces
export interface PatternFilters {
  search: string;
  element_type_id: number | null;
  status: boolean | null;
}

export interface PatternListState {
  patterns: Pattern[];
  loading: boolean;
  error: string | null;
  filters: PatternFilters;
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
}

// Component props interfaces
export interface PatternCardProps {
  pattern: Pattern;
  elementTypes: ElementType[];
  onEdit: (pattern: Pattern) => void;
  onDelete: (pattern: Pattern) => void;
  onToggleStatus: (pattern: Pattern) => void;
  onUpload: (pattern: Pattern, view: 'front' | 'side') => void;
}

export interface PatternFormProps {
  pattern?: Pattern;
  elementTypes: ElementType[];
  onSubmit: (data: PatternFormData) => void;
  onCancel: () => void;
  loading?: boolean;
}

export interface PatternUploadProps {
  pattern: Pattern;
  view: 'front' | 'side';
  onUpload: (file: File) => void;
  onCancel: () => void;
  loading?: boolean;
}

export interface PatternPreviewProps {
  pattern: Pattern;
  view: 'front' | 'side';
  size?: 'small' | 'medium' | 'large';
  showFallback?: boolean;
}

// Action types for pattern management
export type PatternAction = 
  | 'create'
  | 'edit'
  | 'delete'
  | 'upload'
  | 'toggle-status'
  | 'view';

export interface PatternActionData {
  action: PatternAction;
  pattern?: Pattern;
  view?: 'front' | 'side';
}

// Validation interfaces
export interface PatternValidationErrors {
  description?: string;
  element_type_id?: string;
  matching_string?: string;
  status?: string;
  file?: string;
  front_svg_file?: string;
  side_svg_file?: string;
}

export interface PatternFormValidation {
  isValid: boolean;
  errors: PatternValidationErrors;
}

// Constants
export const PATTERN_CONSTANTS = {
  MAX_DESCRIPTION_LENGTH: 255,
  MAX_MATCHING_STRING_LENGTH: 255,
  ALLOWED_FILE_TYPES: ['image/svg+xml'] as const,
  ALLOWED_FILE_EXTENSIONS: ['.svg'] as const,
  MAX_FILE_SIZE: 5 * 1024 * 1024, // 5MB
  PREVIEW_SIZES: {
    small: { width: 80, height: 100 },
    medium: { width: 120, height: 150 },
    large: { width: 200, height: 250 },
  },
} as const;

// Utility types
export type PatternStatus = 'active' | 'inactive';
export type PatternView = 'front' | 'side';
export type PatternPreviewSize = keyof typeof PATTERN_CONSTANTS.PREVIEW_SIZES;
